"use client";

import React, { useCallback, useRef, useState } from "react";
import Image from "next/image";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { motion, useScroll, useTransform } from "framer-motion";
import ShareButton from "@/src/shared/components/ShareButton";
import { WishlistHeartButton } from "@/src/shared/features/wishlist";
import type { IImage } from "@/src/shared/types/listingPrice";
import { cn } from "@/src/lib/utils";
import { heroDots } from "@/src/shared/services/heroGallery";

/** The app's hero: 300px of photo, a white sheet overlapping its last 24px. */
export const HERO_HEIGHT = 300;
const SHEET_OVERLAP = 24;
/** The collapsed bar's height: the photo is "scrolled away" once this much is left. */
const BAR_HEIGHT = 56;
/** Bubbles and "Show more" sit this far above the sheet. */
const CONTROLS_INSET = 16;

interface IHeroGalleryProps {
  /** The listing (or hotel portfolio) id the heart saves. */
  listingId: number;
  images: IImage[];
  /** The page to share; the current one when absent. */
  shareUrl?: string;
  /** Opens every photo (the app's image list). */
  onOpenGallery: () => void;
  /**
   * The app's two "Show more" buttons: listing details has a small 24px tag ("Show more",
   * 10px); hotel details its 36px `ImagePillButton` ("Show More", 12px), like Share.
   */
  showMore?: "tag" | "pill";
}

const SHOW_MORE = {
  tag: {
    label: "Show more",
    className:
      "h-6 rounded-lg px-4 text-10 font-medium leading-[14px] tracking-[0.2px] text-ink",
  },
  pill: {
    label: "Show More",
    className: "h-9 rounded-[20px] pl-3 pr-4 text-xs font-medium text-black",
  },
} as const;

/**
 * A listing's or hotel's photo hero on mobile, as the app's `ListingHeroHeader`: the photos swipe
 * full width under white bubbles and a "Show more" pill. The photo stays pinned while the
 * page's sheet slides up over it, the round back / Share / save buttons fade out on the
 * way, and pulling down at the top (iOS) stretches the photo. Motion values drive the
 * scroll effects, so scrolling re-renders nothing.
 */
const HeroGallery: React.FC<IHeroGalleryProps> = ({
  listingId,
  images,
  shareUrl,
  onOpenGallery,
  showMore = "tag",
}) => {
  const router = useAppRouter();
  const { scrollY } = useScroll();
  // Pinned: the photo keeps its place while the page scrolls over it; above the top
  // (a negative offset, iOS's pull-down) it keeps its top edge and grows instead.
  const pinY = useTransform(scrollY, (y) =>
    y < 0 ? y : Math.min(y, HERO_HEIGHT),
  );
  const stretch = useTransform(scrollY, (y) =>
    y < 0 ? 1 + -y / HERO_HEIGHT : 1,
  );
  // The app fades the round buttons out over the first 40% of the collapse.
  const actionsOpacity = useTransform(
    scrollY,
    [0, (HERO_HEIGHT - BAR_HEIGHT) * 0.4],
    [1, 0],
  );

  // The swipe position, fractional mid-swipe, so the bubbles follow the finger.
  const [position, setPosition] = useState(0);
  const frame = useRef(0);
  const onSwipe = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const track = event.currentTarget;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() =>
      setPosition(track.scrollLeft / Math.max(track.clientWidth, 1)),
    );
  }, []);

  const goBack = () => {
    if (window.history.length > 1) router.back();
    else void router.push("/");
  };

  const dots = heroDots(images.length, position);
  const controlsBottom = SHEET_OVERLAP + CONTROLS_INSET;

  return (
    <div className="relative md:hidden" style={{ height: HERO_HEIGHT }}>
      <motion.div
        className="absolute inset-x-0 top-0 origin-top"
        style={{ height: HERO_HEIGHT, y: pinY, scale: stretch }}
      >
        <div
          onScroll={onSwipe}
          className="flex h-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain scrollbar-hide"
        >
          {(images.length ? images : [null]).map((image, index) => (
            <button
              key={image?.id ?? "empty"}
              type="button"
              onClick={onOpenGallery}
              aria-label={`Photo ${index + 1} of ${images.length}`}
              className="relative h-full w-full shrink-0 snap-center bg-[#F2F2F2]"
            >
              {image && (
                <Image
                  src={image.url}
                  alt=""
                  fill
                  sizes="100vw"
                  priority={index === 0}
                  // The photo flight waits for this one before stepping aside.
                  data-hero-photo={index === 0 ? "" : undefined}
                  className="object-cover"
                />
              )}
            </button>
          ))}
        </div>

        {images.length > 1 && (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 flex items-center justify-center gap-1"
              style={{ bottom: controlsBottom }}
            >
              {dots.map((dot) => (
                <span
                  key={dot.index}
                  className="h-2 rounded-full bg-white shadow-[0_0_2px_rgba(0,0,0,0.25)] transition-transform duration-200"
                  style={{
                    width: dot.width,
                    transform: `scale(${dot.scale})`,
                  }}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={onOpenGallery}
              className={cn(
                "absolute right-4 flex items-center bg-white",
                SHOW_MORE[showMore].className,
              )}
              style={{ bottom: controlsBottom }}
            >
              {SHOW_MORE[showMore].label}
            </button>
          </>
        )}

        {/* The app's round buttons over the photo; they fade as the bar comes in. */}
        <motion.div
          style={{ opacity: actionsOpacity }}
          className="absolute inset-x-0 top-0 flex h-14 items-center gap-3 px-4 pt-[env(safe-area-inset-top)]"
        >
          <button
            type="button"
            onClick={goBack}
            aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm"
          >
            <img
              src="/icons/listing/listing_hero_back.svg"
              alt=""
              className="h-[18px] w-[18px]"
            />
          </button>
          <span className="flex-1" />
          <ShareButton variant="hero" url={shareUrl} />
          <WishlistHeartButton
            listingId={listingId}
            variant="plain"
            size={18}
            className={cn("h-9 w-9 rounded-full bg-white shadow-sm")}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HeroGallery;
